import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCurrentImpersonation, type ImpersonationSessionSummary } from '../api/impersonation.js';

export interface UseImpersonationSessionResult {
  session: ImpersonationSessionSummary | null;
  isLoading: boolean;
  refetch: () => void;
}

/**
 * Polls /api/v1/admin/impersonation/current every 30 seconds and listens for
 * the `impersonation:refetch` window event for immediate updates (fired by the
 * banner component after toggling write mode or exiting).
 *
 * Pass `enabled = false` to suspend polling (e.g., when user is not authenticated).
 */
export function useImpersonationSession(enabled = true): UseImpersonationSessionResult {
  const query = useQuery({
    queryKey: ['impersonation', 'current'],
    queryFn: () => getCurrentImpersonation(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    enabled,
  });

  useEffect(() => {
    const handler = () => {
      void query.refetch();
    };
    window.addEventListener('impersonation:refetch', handler);
    return () => {
      window.removeEventListener('impersonation:refetch', handler);
    };
  }, [query]);

  return {
    session: query.data ?? null,
    isLoading: query.isLoading,
    refetch: () => {
      void query.refetch();
    },
  };
}
